import {
  Card,
  Chip,
  ChipGroup,
  Flex,
  List,
  DataListItem,
  Tag,
  Text,
  Grid,
  NumberEasing,
} from '@/components'
import { TemperatureBar } from '@/app/features/smart-thermostats'
import { isDecimal } from '@/utils/number'
import { positiveClamp } from '@/utils/limit'
import { centsToDollars } from '@/utils/currency'
import { CostSheet } from '@/app/components'
import useThermostatCostSavingsViewModel, {
  Props as ViewModelProps,
  ThermostatCostSavingsViewModel,
} from './useThermostatCostSavingsViewModel'
import {
  TemperatureSkeleton,
  TemperatureBarSkeleton,
  CostSheetSkeleton,
} from './ThermostatCostSavingsCard.skeletons'
import EmptySavings from '@/app/img/empty-savings.svg'
import React from 'react'

export interface Props extends ViewModelProps {
  title: string
  barGraphComponent?: React.ComponentType<ThermostatCostSavingsViewModel>
  showCostOfSchedule?: boolean
  showCostOfAdjustments?: boolean
  showAvgSetPoint?: boolean
  showAvgOutsideTemp?: boolean
  infoModalComponent?: React.ComponentType<{
    viewModel: ThermostatCostSavingsViewModel
  }>
  savingsChipComponent?: React.ComponentType<{
    monthSavingsAmount: number
    loading?: boolean
  }>
}

export default function ThermostatCostSavingsCard(props: Props) {
  const viewModel = useThermostatCostSavingsViewModel(props)

  const {
    title,
    barGraphComponent: ThermostatCostSavingsBarGraphComponent,
    showCostOfSchedule = false,
    showCostOfAdjustments = false,
    showAvgSetPoint = true,
    showAvgOutsideTemp = true,
    infoModalComponent: InfoModalComponent,
    savingsChipComponent: SavingsChipComponent,
  } = props

  // Check if there are no savings in the month period (only for Total Savings)
  const hasNoMonthSavings =
    title === 'Total Savings' &&
    !viewModel.loading &&
    (viewModel.monthCostOfOptiwattAdjustmentsCents >= 0 ||
      viewModel.monthSavingsAmount === 0)

  if (hasNoMonthSavings) {
    return (
      <div className="bg-white rounded-2.5xl shadow-spread">
        <div className="flex flex-col gap-2 p-4 text-center bg-[#FBF6FF] rounded-2xl">
          <img src={EmptySavings} className="size-36 mx-auto" />
          <Text variant="h3">No Savings Currently</Text>
          <Text variant="body2" className="text-base-grey-900">
            Check back soon to see new ways to save with your device!
          </Text>
        </div>
      </div>
    )
  }

  return (
    <Card>
      <Grid flow="row" gap="24px">
        {InfoModalComponent || SavingsChipComponent ? (
          <Flex
            container
            alignItems="center"
            justifyContent="space-between"
            wrap="nowrap"
          >
            <Flex container alignItems="center" gap="8px" wrap="nowrap">
              <Text variant="h3">{title}</Text>
              {InfoModalComponent && (
                <InfoModalComponent viewModel={viewModel} />
              )}
            </Flex>
            {SavingsChipComponent && (
              <Flex item>
                <SavingsChipComponent
                  monthSavingsAmount={viewModel.monthSavingsAmount}
                  loading={viewModel.loading}
                />
              </Flex>
            )}
          </Flex>
        ) : (
          <Text variant="h3">{title}</Text>
        )}
        <ChipGroup
          disabled={viewModel.loading}
          onClick={viewModel.onChipGroupClick}
          required
          scrollable
          initialValue={
            viewModel.period === 'day' ? 0 : viewModel.period === 'week' ? 1 : 2
          }
          key={viewModel.period}
        >
          <Chip>
            <Text>Today</Text>
          </Chip>
          <Chip>
            <Text>Last 7 Days</Text>
          </Chip>
          <Chip>
            <Text>Last 30 Days</Text>
          </Chip>
        </ChipGroup>
        {ThermostatCostSavingsBarGraphComponent && (
          <ThermostatCostSavingsBarGraphComponent {...viewModel} />
        )}
        <List bordered>
          {showCostOfSchedule && (
            <DataListItem label="Cost of Schedule">
              {viewModel.loading ? (
                <CostSheetSkeleton />
              ) : (
                <Tag color="green-100">
                  <Text className="text-themed-green-900">
                    $
                    <NumberEasing
                      decimals={
                        isDecimal(
                          centsToDollars(
                            positiveClamp(viewModel.costOfScheduleCents),
                          ),
                        )
                          ? 2
                          : 0
                      }
                      value={centsToDollars(
                        positiveClamp(viewModel.costOfScheduleCents),
                      )}
                      speed={400}
                      ease="quintOut"
                    />
                  </Text>
                </Tag>
              )}
            </DataListItem>
          )}
          {showCostOfAdjustments && (
            <DataListItem label="Cost of Adjustments">
              {viewModel.loading ? (
                <CostSheetSkeleton />
              ) : (
                <CostSheet cost={viewModel.costOfAdjustmentsCents} />
              )}
            </DataListItem>
          )}
          {showAvgSetPoint && (
            <DataListItem label="Avg. Set Point">
              {viewModel.loading ? (
                <TemperatureSkeleton />
              ) : (
                <Text>
                  <NumberEasing
                    value={positiveClamp(viewModel.averageSetPoint)}
                    speed={400}
                    ease="quintOut"
                  />
                  °
                </Text>
              )}
            </DataListItem>
          )}
          {showAvgOutsideTemp && (
            <DataListItem label="Avg. Outside Temp">
              <Flex item>
                {viewModel.loading ? (
                  <TemperatureBarSkeleton />
                ) : (
                  <TemperatureBar
                    high={viewModel.avgHighTempF}
                    low={viewModel.avgLowTempF}
                    max={viewModel.maxTempF}
                    min={viewModel.minTempF}
                  />
                )}
              </Flex>
            </DataListItem>
          )}
        </List>
      </Grid>
    </Card>
  )
}
